import { useCallback, useEffect, useState } from "react";
import api from "../../../common/utils/api";
import { useParams } from "react-router";
import { enqueueSnackbar } from "notistack";
import LoadingFallback from "../../../common/components/LoadingFallback";
import { Box, Button, Card, CardContent, Container, Tooltip, Typography } from "@mui/material";
import { Check, DoNotDisturb, Help } from "@mui/icons-material";
import AccessIssue from "./AccessIssue";

const initAccessState = {
  project: {},
  client: {},
};

export default function AccessValidation() {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [accessState, setAccessState] = useState(initAccessState);
  const [errors, setErrors] = useState([]);

  const handleLoad = useCallback(() => {
    setLoading(true);

    api
      .get(`/portal/access-validation/${params.projectId}`)
      .then((resp) => {
        if (resp.data?.state) {
          setAccessState({ ...initAccessState, ...resp.data.state });
        }
        if (resp.data?.errors) {
          setErrors(resp.data.errors);
        }
      })
      .catch((err) => {
        enqueueSnackbar(err.response?.data?.error || err.message || "Error loading validation check");
      })
      .finally((_) => {
        setLoading(false);
      });
  }, [params.projectId]);

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return <LoadingFallback />;
  }
  
  return (
    <Container sx={{ minHeight: "90vh", gap: 2, display: "flex", flexDirection: "column" }} maxWidth="md">
      <Card>
        <CardContent>
          <Typography variant="h5">Client Portal Status </Typography>
          <Typography>{accessState?.project?.projectName}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6" mb={2}>
            Access Requirements
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Typography>
              Client Access Granted{" "}
              <Tooltip title="Set Client Portal Access in Contact Card Details Tab">
                <Help fontSize="0.8rem" />
              </Tooltip>
            </Typography>
            <Box>
              <Box>{accessState.clientPortalAccess ? <Check color="success" /> : <DoNotDisturb />}</Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Typography>
              Project Access Granted{" "}
              <Tooltip title="Set Project Portal Access on the Case Summary section of the project">
                <Help fontSize="0.8rem" />
              </Tooltip>
            </Typography>
            <Box>{accessState.projectPortalAccess ? <Check color="success" /> : <DoNotDisturb />}</Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Typography>Account Activated</Typography>
            <Box>
              <Box>{accessState.accountActivated ? <Check color="success" /> : <DoNotDisturb />}</Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6">Access Issues</Typography>
          {!accessState.clientDBError &&
            !(accessState.accountActivated && accessState.missingActivationTokenError) &&
            !accessState.projectSectionsDBError &&
            !accessState.invalidEmailAddressError &&
            !accessState.missingClientPersonTypeError &&
            !accessState.errors && <>None</>}
          {accessState.clientDBError && (
            <AccessIssue
              projectId={params.projectId}
              onFix={handleLoad}
              description="Required information regarding Client Portal Access is missing from the db."
              endpoint="fix-client-db"
            />
          )}
          {accessState.accountActivated && accessState.missingActivationTokenError && (
            <AccessIssue
              projectId={params.projectId}
              onFix={handleLoad}
              description="Missing activation token. A token is required for the client to activate their account."
              endpoint="fix-activation-token"
            />
          )}
          {accessState.projectSectionsDBError && (
            <AccessIssue
              projectId={params.projectId}
              onFix={handleLoad}
              description="Required information regarding in db missing."
              endpoint="fix-project-section-db"
            />
          )}

          {accessState.invalidEmailAddressError && (
            <AccessIssue
              projectId={params.projectId}
              onFix={handleLoad}
              description="Client is missing valid email address."
            />
          )}
          {accessState.missingClientPersonTypeError && (
            <AccessIssue
              projectId={params.projectId}
              onFix={handleLoad}
              description="Contact on project does not have 'Client' tag."
            />
          )}
          {errors?.map((v, i) => (
            <AccessIssue key={i} description={v} />
          ))}
        </CardContent>
      </Card>
      {/* <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6">Assume Identity</Typography>
          <Box>
            Assming identity puts you into the Portal as this client. Use cautiously and only for troubleshooting.
          </Box>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Button variant="outlined">Assume Identity of Client</Button>
          </Box>
        </CardContent>
      </Card> */}

      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button variant="contained" color="primary" onClick={handleClose}>
          Close
        </Button>
      </Box>
    </Container>
  );
}
